import { format, parseISO } from 'date-fns';
import { enUS, sv } from 'date-fns/locale';

const locales = { en: enUS, sv: sv };

/**
 * Format a date string according to the specified language locale
 * @param dateString - ISO date string (e.g., "2024-05-15") or pre-formatted string
 * @param language - 'en' or 'sv'
 * @param formatStr - Optional custom format string for English (Swedish uses 'd MMMM yyyy')
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string, 
  language: 'en' | 'sv',
  formatStr: string = 'MMMM d, yyyy'
): string => {
  try {
    // Check if it's already an ISO date format
    const date = parseISO(dateString);
    
    // If invalid date, return original string
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const locale = locales[language];
    
    // Swedish uses different format: "15 maj 2024"
    const localizedFormat = language === 'sv' ? 'd MMMM yyyy' : formatStr;
    
    return format(date, localizedFormat, { locale });
  } catch {
    // If parsing fails, return original string
    return dateString;
  }
};

/**
 * Format a date for blog posts with short month
 * @param dateString - ISO date string
 * @param language - 'en' or 'sv'
 * @returns Formatted date string with short month
 */
export const formatDateShort = (
  dateString: string,
  language: 'en' | 'sv'
): string => {
  try {
    const date = parseISO(dateString);
    
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const locale = locales[language];
    const localizedFormat = language === 'sv' ? 'd MMM yyyy' : 'MMM d, yyyy';
    
    return format(date, localizedFormat, { locale });
  } catch {
    return dateString;
  }
};
